// @ts-check
import React from 'react';
import { ErrorMessage, useField } from 'formik';
import Spinner from 'shared/components/Spinner';
import { SpinnerContainer } from '../SpinnerContainer';
import {
  VerticalField,
  HorizontalField,
} from 'preferral-ui/components/FieldStructure';
import './PlainSelectField.css';

/** @typedef {{
  value: string;
  label: string;
}} StandardOption */

/** @typedef {{
  value?: string;
  options: StandardOption[];
  isLoading?: boolean;
  inputProps?: React.InputHTMLAttributes<HTMLSelectElement>;
  className?: string;
  onChange?(newValue: string): void;
  onBlur?(e: any): void;
  onFocus?(e: any): void;
}} InputPlainSelectProps */

/** @type { React.FC<InputPlainSelectProps> } */
export const InputPlainSelect = (props) => {
  const {
    value,
    options,
    onChange,
    onBlur,
    onFocus,
    isLoading = false,
    inputProps = {},
    className = '',
  } = props;

  /**
   *
   * @param {React.ChangeEvent<HTMLSelectElement>} evt
   */
  function handleChange(evt) {
    if (onChange) onChange(evt.target.value);
  }

  /**
   * NB: `className` can be overridden by `inputProps`.
   */

  return (
    <div className="rounded-md shadow-sm">
      <select
        className={`InputPlainSelect block form-select w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5 ${className}`}
        onChange={handleChange}
        onBlur={onBlur}
        onFocus={onFocus}
        value={value}
        {...inputProps}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {isLoading && (
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      )}
    </div>
  );
};

/** @typedef {{
  id?: string;
  name: string;
  placeholder?: string;
  options: StandardOption[];
  disabled?: boolean;
  inputProps?: React.InputHTMLAttributes<HTMLSelectElement>;
  isLoading?: boolean;
  autoFocus?: boolean;
  className?: string;
  onFocus?(e: any): void;
}} PlainSelectInputProps */

/** @type { React.FC<PlainSelectInputProps> } */
export const PlainSelectInput = (props) => {
  const {
    id,
    name,
    placeholder,
    autoFocus = false,
    className = '',
    inputProps = {},
    options,
    isLoading = false,
    onFocus,
  } = props;

  const [field, meta, helpers] = useField(name);
  const { value, onBlur } = field;
  const { setValue } = helpers;

  return (
    <>
      <InputPlainSelect
        inputProps={{
          id: id || name,
          placeholder,
          name,
          autoFocus,
          ...inputProps,
        }}
        className={
          meta && meta.touched && meta.error
            ? `${className} border border-red-500`
            : className
        }
        value={value}
        options={options}
        onChange={setValue}
        onBlur={onBlur}
        onFocus={onFocus}
        isLoading={isLoading}
      />
      <ErrorMessage
        component="p"
        name={name}
        className="mt-2 text-xs italic text-red-500"
      />
    </>
  );
};

/**
 * PlainSelectField.
 */

/** @typedef {{
  label: string;
  indicateOptional?: boolean;
} & PlainSelectInputProps} PlainSelectFieldProps */

/** @type { React.FC<PlainSelectFieldProps> } */
export const PlainSelectField = (props) => {
  const { label, indicateOptional, ...rest } = props;

  return (
    <VerticalField
      id={`field--${rest.id || rest.name}`}
      htmlFor={rest.id || rest.name}
      label={label}
      indicateOptional={indicateOptional}
    >
      <PlainSelectInput {...rest} />
    </VerticalField>
  );
};

/** @type { React.FC<PlainSelectFieldProps> } */
export const HorizontalPlainSelectField = (props) => {
  const { label, indicateOptional, ...rest } = props;

  return (
    <HorizontalField
      id={`field--${rest.id || rest.name}`}
      htmlFor={rest.id || rest.name}
      label={label}
      indicateOptional={indicateOptional}
    >
      <PlainSelectInput {...rest} />
    </HorizontalField>
  );
};
